import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { validateImportFile, describeImportError } from "@/lib/importFileValidator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Upload, Search, Package, Camera, ScanBarcode, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import ProductSheet from "@/components/ProductSheet";
import SaleSheet from "@/components/SaleSheet";
import ReceiptScanner from "@/components/ReceiptScanner";
import BarcodeScanner from "@/components/BarcodeScanner";
import { BARCODE_ENABLED } from "@/lib/featureFlags";
import ExportMenu from "@/components/ExportMenu";
import ProductCard from "@/components/products/ProductCard";
import ProductRow from "@/components/products/ProductRow";
import LotsList from "@/components/products/LotsList";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePerm } from "@/context/AuthContext";

export default function Products() {
  const canWrite = usePerm("products_write");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editProduct, setEditProduct] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saleOpen, setSaleOpen] = useState(false);
  const [sellingProduct, setSellingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Tabella desktop: una sola riga espansa alla volta per esporre la LotsList
  // dei prodotti con 2+ lotti. Soluzione più semplice; se poi servisse
  // multi-expand basta cambiare in un Set.
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [stagnantIds, setStagnantIds] = useState(null);
  // Import preview: mostra all'utente il mapping colonne rilevato prima di committare l'import.
  const [importPreview, setImportPreview] = useState(null); // { file, columns, preview, total_rows }
  const [importResult, setImportResult] = useState(null); // rapporto post-import: {created, updated, updated_with_ignored, warnings, total_warnings, total_errors, errors}
  const [importing, setImporting] = useState(false);

  const activeFilter = searchParams.get("filter"); // 'expiring' | 'expired' | 'stagnant' | 'low_stock' | null

  // Ricerca server-side (nome + lotto + PLU + codice fornitore). Debounce 250ms
  // e guardia anti-race con contatore monotonico: una risposta in ritardo di una
  // ricerca precedente non deve sovrascrivere una richiesta più recente.
  const searchSeqRef = useRef(0);

  const load = async () => {
    const [p, c] = await Promise.all([api.listProducts(), api.listCategories()]);
    setProducts(p);
    setCategories(Array.from(new Set(c || [])));
    return p;
  };

  useEffect(() => {
    load();
  }, []);

  // Ricarica prodotti quando cambia il termine di ricerca (server-side).
  useEffect(() => {
    const term = search.trim();
    const mySeq = ++searchSeqRef.current;
    const t = setTimeout(() => {
      api.listProducts(term ? { search: term } : {}).then((data) => {
        if (mySeq !== searchSeqRef.current) return; // risposta obsoleta → scarta
        setProducts(data || []);
      }).catch(() => {
        if (mySeq !== searchSeqRef.current) return;
        setProducts([]);
      });
    }, term ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Apri direttamente la scheda prodotto se arrivi da un avviso (?open=<id>)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    if (!products.length) return; // aspetta che i prodotti siano caricati
    const target = products.find((p) => p.id === openId);
    if (target) {
      setEditProduct(target);
      setSheetOpen(true);
    } else {
      toast.error("Prodotto non trovato", { description: "Potrebbe essere stato eliminato." });
    }
    // Rimuovi il param per non riaprire la scheda ad ogni cambio di stato
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchParams]);

  // Quando l'utente arriva con ?filter=stagnant, fetcha la lista dei prodotti fermi
  // dall'analytics endpoint (più affidabile dell'incrocio frontend con le vendite).
  useEffect(() => {
    if (activeFilter !== "stagnant") {
      setStagnantIds(null);
      return;
    }
    (async () => {
      try {
        const a = await api.salesAnalytics(30);
        const ids = new Set((a?.stagnant_products || []).map((p) => p.product_id));
        setStagnantIds(ids);
      } catch (_e) {
        setStagnantIds(new Set());
      }
    })();
  }, [activeFilter]);

  const filtered = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in7 = new Date(today); in7.setDate(today.getDate() + 7);
    return products.filter((p) => {
      const catOk = categoryFilter === "all" || p.category === categoryFilter;
      // Filtri preset (da query string)
      let presetOk = true;
      if (activeFilter === "expiring") {
        if (!p.expiry_date) { presetOk = false; }
        else {
          const d = new Date(`${p.expiry_date}T00:00:00`);
          presetOk = !Number.isNaN(d.getTime()) && d >= today && d <= in7;
        }
      } else if (activeFilter === "expired") {
        if (!p.expiry_date) { presetOk = false; }
        else {
          const d = new Date(`${p.expiry_date}T00:00:00`);
          presetOk = !Number.isNaN(d.getTime()) && d < today;
        }
      } else if (activeFilter === "stagnant") {
        presetOk = stagnantIds ? stagnantIds.has(p.id) : true;
      } else if (activeFilter === "low_stock") {
        // Coerente con backend server.py:2526 (KPI insight low_stock):
        // min_stock impostato AND stock > 0 AND stock <= min_stock.
        // Gli esauriti (stock <= 0) sono esclusi per non duplicare l'informazione.
        const min = Number(p.min_stock);
        const s = Number(p.stock);
        presetOk = p.min_stock !== null && p.min_stock !== undefined && s > 0 && s <= min;
      }
      return catOk && presetOk;
    });
  }, [products, categoryFilter, activeFilter, stagnantIds]);

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const filterMeta = {
    expiring: { label: "In scadenza nei prossimi 7 giorni", tone: "bg-[#FDF6E3] border-[#F2DEB0] text-[#8B6518]" },
    expired: { label: "Già scaduti", tone: "bg-[#FCEAE8] border-[#F2C4BE] text-[#8E3B2A]" },
    stagnant: { label: "Fermi (oltre 30 giorni)", tone: "bg-[#EDF5F9] border-[#C4DCE7] text-[#2F5A78]" },
    low_stock: { label: "Sotto scorta minima", tone: "bg-[#FDF6E3] border-[#F2DEB0] text-[#8B6518]" },
  }[activeFilter];

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    // Reset subito il valore così l'onChange scatta anche se l'utente
    // riseleziona lo stesso file (comportamento standard degli input file).
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const check = validateImportFile(file);
    if (!check.ok) {
      toast.error("File non valido", { description: check.message });
      return;
    }
    try {
      const prev = await api.previewProductsImport(file);
      setImportPreview({ file, ...prev });
    } catch (err) {
      toast.error("Errore lettura file", { description: describeImportError(err) });
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    try {
      const res = await api.importProducts(importPreview.file);
      const nuovi = res.created ?? res.inserted ?? 0;
      const aggiornati = res.updated ?? 0;
      const conAvvisi = res.updated_with_ignored ?? 0;
      const errori = res.total_errors ?? 0;

      const counters = [];
      if (nuovi > 0) counters.push(`${nuovi} nuovi`);
      if (aggiornati > 0) counters.push(`${aggiornati} aggiornati`);
      if (counters.length === 0) counters.push("nessuna riga");

      if (errori > 0) {
        // Toast di ERRORE (non verde). Prima riga dichiara errori.
        // Coda: aggiornati/nuovi, poi ERRORI (per primi), poi avvisi.
        const tail = [...counters];
        tail.push(errori === 1 ? "1 riga non importata" : `${errori} righe non importate`);
        if (conAvvisi > 0) tail.push(`${conAvvisi} con avvisi`);
        toast.error(`Import completato con errori: ${tail.join(", ")}`);
      } else if (conAvvisi > 0) {
        // Toast di AVVISO (non verde), niente riga di dettaglio: sta nel Rapporto.
        counters.push(`${conAvvisi} con avvisi`);
        toast.warning(`Import completato: ${counters.join(", ")}`);
      } else {
        // Toast di SUCCESSO verde con "Tutto ok".
        toast.success(`Import completato: ${counters.join(", ")}`, {
          description: "Tutto ok",
        });
      }
      // Se ci sono warnings o errori, mostriamo il Rapporto in-place. Altrimenti
      // chiudiamo il Dialog come prima.
      if ((res.total_warnings ?? 0) > 0 || errori > 0) {
        setImportResult(res);
        setImportPreview(null);
      } else {
        setImportPreview(null);
      }
      load();
    } catch (err) {
      toast.error("Errore import", { description: describeImportError(err) });
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editProduct?.id) {
        const updated = await api.updateProduct(editProduct.id, data);
        // Guardia multi-lotto: se il backend ha ignorato i campi cache
        // (stock/stock_kg/batch/expiry_date/cost_*) perché il prodotto ha
        // più lotti, avvisa l'operatore in giallo, elencando i campi.
        // Il salvataggio degli altri campi resta valido.
        const ignored = Array.isArray(updated?.ignored_fields) ? updated.ignored_fields : [];
        if (ignored.length > 0) {
          const IT_LABEL = {
            batch: "lotto",
            expiry_date: "scadenza",
            stock: "giacenza",
            stock_kg: "giacenza",
            cost_price: "costo",
            cost_per_kg: "costo",
          };
          const humanList = Array.from(
            new Set(ignored.map((f) => IT_LABEL[f] || f))
          ).join(", ");
          toast.warning(`Salvato. Non applicati: ${humanList}`, {
            description:
              "Questo prodotto ha più lotti. Per modificarli, apri il singolo lotto.",
          });
        } else {
          toast.success("Prodotto aggiornato");
        }
      } else {
        await api.createProduct(data);
        toast.success("Prodotto creato");
      }
      setSheetOpen(false);
      setEditProduct(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteProduct(deleteTarget.id);
      toast.success("Prodotto eliminato");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const handleBarcodeDetected = (code) => {
    setBarcodeOpen(false);
    if (!code) return;
    const found = products.find((p) => (p.barcode || "").trim() === code.trim());
    if (found) {
      toast.success("Prodotto trovato", { description: `${found.name} · barcode ${code}` });
      setEditProduct(found);
      setSheetOpen(true);
    } else {
      toast.info("Nuovo barcode rilevato", { description: `Compila i dati per ${code}` });
      // Pre-fill the new product with the scanned barcode (ProductSheet uses product prop to populate form)
      setEditProduct({ barcode: code });
      setSheetOpen(true);
    }
  };


  return (
    <div data-testid="products-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">Magazzino</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Prodotti
          </h1>
          <p className="text-[#5C5F5A] mt-2">
            {products.length} prodotti · {filtered.length} visibili
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            data-testid="import-file-input"
            type="file"
            /* Niente `accept`: su Android il picker filtra per MIME e i CSV
               spesso hanno MIME "sbagliati" (octet-stream, vnd.ms-excel…),
               con il risultato che l'elenco file appare VUOTO. Mostriamo
               tutto e validiamo dopo, sull'estensione. */
            className="hidden"
            onChange={handleImport}
          />
          <TooltipProvider delayDuration={200}>
            {BARCODE_ENABLED && canWrite && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="scan-barcode-btn"
                  onClick={() => setBarcodeOpen(true)}
                  variant="outline"
                  className="border-[#EAE7E1] bg-white hover:bg-[#F4F1ED]"
                >
                  <ScanBarcode size={16} className="mr-2" />
                  Scansiona barcode
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <span className="text-xs">Inquadra il codice EAN/UPC: cerca il prodotto se esiste, altrimenti precompila il barcode in un nuovo prodotto.</span>
              </TooltipContent>
            </Tooltip>
            )}
          </TooltipProvider>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="scan-product-btn"
                  onClick={() => setScannerOpen(true)}
                  variant="outline"
                  className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA]"
                >
                  <Camera size={16} className="mr-2" />
                  Scansiona scontrino
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <span className="text-xs">Fotografa uno scontrino o DDT: l&apos;AI estrae ogni prodotto, prezzo e scadenza automaticamente.</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            data-testid="import-products-btn"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            className="border-[#EAE7E1] bg-white hover:bg-[#F4F1ED]"
            style={{ display: canWrite ? undefined : "none" }}
          >
            <Upload size={16} className="mr-2" />
            Importa Excel/CSV
          </Button>
          <ExportMenu
            testid="export-products-btn"
            onExport={(fmt) => api.exportProducts(fmt)}
            filenameBase="magazzino_prodotti"
          />
          {canWrite && (
          <Button
            data-testid="add-product-btn"
            onClick={() => {
              setEditProduct(null);
              setSheetOpen(true);
            }}
            className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
          >
            <Plus size={16} className="mr-2" />
            Nuovo prodotto
          </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E918C]" />
          <Input
            data-testid="product-search"
            placeholder="Cerca per nome o lotto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#EAE7E1]"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="category-filter" className="w-full md:w-56 bg-white border-[#EAE7E1]">
            <SelectValue placeholder="Tutte le categorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Filtro attivo (da Dashboard) */}
      {filterMeta && (
        <div
          data-testid={`active-filter-${activeFilter}`}
          className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${filterMeta.tone}`}
        >
          <div className="text-sm font-medium">
            Filtro attivo: <span className="font-semibold">{filterMeta.label}</span>
            <span className="ml-2 text-xs opacity-80">({filtered.length} {filtered.length === 1 ? "prodotto" : "prodotti"})</span>
          </div>
          <button
            type="button"
            data-testid="clear-filter-btn"
            onClick={clearFilter}
            className="text-xs underline hover:no-underline opacity-80 hover:opacity-100"
          >
            Mostra tutti
          </button>
        </div>
      )}

      {/* Mobile cards (visible up to lg) */}
      <div className="lg:hidden space-y-3">
        {filtered.length === 0 ? (
          <div className="mag-card p-12 text-center">
            <div className="flex flex-col items-center gap-3 text-[#8E918C]">
              <Package size={32} />
              <div className="text-sm">Nessun prodotto trovato</div>
            </div>
          </div>
        ) : (
          filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              onSell={() => {
                setSellingProduct(p);
                setSaleOpen(true);
              }}
              onEdit={() => {
                setEditProduct(p);
                setSheetOpen(true);
              }}
              onDelete={() => setDeleteTarget(p)}
              onChanged={load}
            />
          ))
        )}
      </div>

      {/* Desktop table (lg+) */}
      <div className="mag-card overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F1ED] hover:bg-[#F4F1ED] border-[#EAE7E1]">
                <TableHead className="w-8 pr-0" />
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Prodotto</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Categoria</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Peso</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Lotto</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Scadenza</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Stock</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold">Prezzo</TableHead>
                <TableHead className="text-[#5C5F5A] text-xs uppercase tracking-wider font-semibold text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3 text-[#8E918C]">
                      <Package size={32} />
                      <div className="text-sm">Nessun prodotto trovato</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((p) => {
                  const isExpanded = expandedRowId === p.id;
                  // Allineato a ProductRow.canExpand (meaningfulLots >= 1):
                  // il chevron è mostrato anche per prodotti con 1 solo lotto reale,
                  // così il pannello espanso (LotsList con matita) è coerente.
                  const meaningfulLots = Array.isArray(p.lots)
                    ? p.lots.filter((l) => !((l.batch || "") === "" && !l.expiry_date && Number(l.stock || 0) === 0 && Number(l.stock_kg || 0) === 0))
                    : [];
                  const hasLots = meaningfulLots.length >= 1;
                  return (
                    <Fragment key={p.id}>
                      <ProductRow
                        product={p}
                        expanded={isExpanded}
                        onToggleExpand={() =>
                          setExpandedRowId((prev) => (prev === p.id ? null : p.id))
                        }
                        onSell={() => {
                          setSellingProduct(p);
                          setSaleOpen(true);
                        }}
                        onEdit={() => {
                          setEditProduct(p);
                          setSheetOpen(true);
                        }}
                        onDelete={() => setDeleteTarget(p)}
                      />
                      {isExpanded && hasLots && (
                        <TableRow
                          data-testid={`product-row-lots-${p.id}`}
                          className="bg-[#F9F8F6] border-[#EAE7E1] hover:bg-[#F9F8F6]"
                        >
                          <TableCell colSpan={9} className="py-3 px-6">
                            <LotsList product={p} onChanged={load} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ReceiptScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        scanType="product"
        onSaved={load}
      />

      <BarcodeScanner
        open={BARCODE_ENABLED && barcodeOpen}
        onOpenChange={setBarcodeOpen}
        onDetected={handleBarcodeDetected}
      />

      <ProductSheet
        open={sheetOpen}
        onOpenChange={(v) => {
          setSheetOpen(v);
          if (!v) setEditProduct(null);
        }}
        product={editProduct}
        categories={categories}
        onSave={handleSave}
        onGoToLots={(productId) => {
          // Espande la LotsList della riga corrispondente. Su desktop apre
          // il pannello sotto la riga; su mobile la LotsList è già inline
          // nella ProductCard, quindi il set qui è innocuo. Scrolliamo alla
          // riga per non lasciare l'utente in cima alla pagina.
          if (productId) {
            setExpandedRowId(productId);
            // Piccolo timeout per lasciare al DOM il tempo di espandere.
            setTimeout(() => {
              const el =
                document.querySelector(`[data-testid="product-row-lots-${productId}"]`) ||
                document.querySelector(`[data-testid="product-card-${productId}"]`);
              if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 60);
          }
        }}
      />

      <SaleSheet
        open={saleOpen}
        onOpenChange={(v) => {
          setSaleOpen(v);
          if (!v) setSellingProduct(null);
        }}
        product={sellingProduct}
        onSaved={() => {
          setSaleOpen(false);
          setSellingProduct(null);
          load();
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleteTarget?.name}&quot; verrà rimosso definitivamente dal magazzino.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={handleDelete}
              className="bg-[#C65D47] hover:bg-[#A64B38]"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import preview: mostra il mapping colonne rilevato + prime righe prima di confermare */}
      <Dialog open={!!importPreview} onOpenChange={(o) => !o && setImportPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima importazione</DialogTitle>
            <DialogDescription>
              {importPreview?.total_rows ?? 0} righe rilevate. Controlla la mappatura delle colonne
              e le prime {Math.min(importPreview?.preview?.length ?? 0, 10)} righe prima di confermare.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              data-testid="import-preview-summary"
              className="rounded border border-[#C5E1D1] bg-[#F3F8F5] p-3 text-sm"
            >
              <div className="font-semibold text-[#2B5A3F]">
                {(importPreview?.to_create ?? 0)} nuovi · {(importPreview?.to_update ?? 0)} aggiornati
              </div>
              <div className="text-xs text-[#5C5F5A] mt-1">
                L&apos;importazione aggiorna i prodotti esistenti (match per ID, o
                per nome+categoria se l&apos;ID non c&apos;è). Le celle vuote nel file
                NON sovrascrivono i valori attuali. Su un prodotto con un solo
                lotto, scorta, lotto, scadenza e costo del file aggiornano
                anche quel lotto. Con più lotti non vengono importati.
              </div>
            </div>
            {(importPreview?.to_update_ignoring_cache ?? 0) > 0 && (
              <div
                data-testid="import-preview-multilot-banner"
                className="rounded border border-[#F3E1A8] bg-[#FFF7E4] p-3 text-sm text-[#6E5113]"
              >
                <span className="font-semibold">
                  ⚠ {importPreview.to_update_ignoring_cache} prodotti su{" "}
                  {importPreview.to_update} hanno più di un lotto
                </span>
                : per questi non verranno importati scorta, lotto, scadenza e
                costo. Dopo l&apos;import correggili dalla matita del lotto,
                sulla card del prodotto.
              </div>
            )}
            <div>
              <div className="text-xs font-semibold text-[#5C5F5A] uppercase tracking-wider mb-2">
                Mappatura colonne
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {importPreview?.columns?.map((c, i) => {
                  const isDuplicate = c.duplicate === true;
                  const isIgnored = c.ignored === true || (!c.recognized && !isDuplicate);
                  let boxCls, Icon, tail;
                  if (c.recognized) {
                    boxCls = "bg-[#E8F3EC] border-[#C5E1D1] text-[#2B5A3F]";
                    Icon = CheckCircle2;
                    tail = <span className="font-semibold">{c.mapped_to}</span>;
                  } else if (isDuplicate) {
                    boxCls = "bg-[#FFF7E4] border-[#F3E1A8] text-[#8A6C1F]";
                    Icon = AlertTriangle;
                    tail = (
                      <span className="font-semibold">
                        {c.mapped_to}{" "}
                        <span className="opacity-70 font-normal">(già mappata sopra)</span>
                      </span>
                    );
                  } else {
                    boxCls = "bg-[#FBEAEA] border-[#EFC5C5] text-[#8A2E2E]";
                    Icon = XCircle;
                    tail = <span className="font-semibold">ignorata</span>;
                  }
                  return (
                    <div
                      key={i}
                      data-testid={`import-preview-col-${i}`}
                      data-recognized={c.recognized ? "true" : "false"}
                      data-ignored={isIgnored ? "true" : "false"}
                      className={`flex items-center gap-2 text-xs p-2 rounded border ${boxCls}`}
                    >
                      <Icon size={14} />
                      <span className="font-mono truncate">{c.original}</span>
                      <span className="opacity-60">→</span>
                      {tail}
                    </div>
                  );
                })}
              </div>
              {importPreview?.columns?.some((c) => c.ignored || (!c.recognized && !c.duplicate)) && (
                <div className="text-xs text-[#8A2E2E] mt-2" data-testid="import-preview-ignored-notice">
                  Le colonne in rosso non verranno importate. Le colonne in giallo
                  sono duplicati della stessa informazione (una sola verrà considerata).
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold text-[#5C5F5A] uppercase tracking-wider mb-2">
                Anteprima righe ({importPreview?.preview?.length ?? 0})
              </div>
              <div className="overflow-x-auto rounded border border-[#EAE7E1]">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F1ED] text-[#5C5F5A]">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Nome</th>
                      <th className="px-2 py-1.5 text-left">Categoria</th>
                      <th className="px-2 py-1.5 text-left">PLU</th>
                      <th className="px-2 py-1.5 text-left">Cod. fornitore</th>
                      <th className="px-2 py-1.5 text-right">Stock</th>
                      <th className="px-2 py-1.5 text-right">Prezzo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview?.preview?.map((r, i) => (
                      <tr key={i} className="border-t border-[#EAE7E1]">
                        <td className="px-2 py-1.5">{r.name}</td>
                        <td className="px-2 py-1.5">{r.category}</td>
                        <td className="px-2 py-1.5 font-mono">{r.plu || "—"}</td>
                        <td className="px-2 py-1.5 font-mono">{r.supplier_code || "—"}</td>
                        <td className="px-2 py-1.5 text-right">{r.stock}</td>
                        <td className="px-2 py-1.5 text-right">€ {Number(r.price || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!importPreview?.preview?.length && (
                <div className="text-xs text-red-600 mt-2">
                  Nessuna riga leggibile. Controlla che il file abbia una colonna nome/descrizione.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportPreview(null)} disabled={importing}>
              Annulla
            </Button>
            <Button
              onClick={confirmImport}
              disabled={importing || !importPreview?.preview?.length}
              className="bg-[#2E4A3B] hover:bg-[#243c2f] text-white"
              data-testid="import-confirm-btn"
            >
              {(() => {
                if (importing) return "Importazione...";
                const nuovi = importPreview?.to_create ?? 0;
                const aggiornati = importPreview?.to_update ?? 0;
                const conAvvisi = importPreview?.to_update_ignoring_cache ?? 0;
                const parts = [`${nuovi} nuovi`, `${aggiornati} aggiornati`];
                if (conAvvisi > 0) parts.push(`${conAvvisi} con avvisi`);
                return `Importa: ${parts.join(", ")}`;
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rapporto finale import: resta a schermo finché l'utente non lo chiude,
          mostra i prodotti con campi ignorati o le righe con errori. Le stringhe
          arrivano già pronte dal backend (`warnings[]`, `errors[]`) — le stampiamo
          così come sono, senza ricomporle. */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle>Rapporto importazione</DialogTitle>
            <DialogDescription>
              {(importResult?.created ?? 0)} nuovi, {(importResult?.updated ?? 0)} aggiornati
              {(importResult?.updated_with_ignored ?? 0) > 0
                ? `, ${importResult.updated_with_ignored} con avvisi`
                : ""}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(importResult?.total_warnings ?? 0) > 0 && (
              <div data-testid="import-result-warnings">
                <div className="text-xs font-semibold text-[#6E5113] uppercase tracking-wider mb-2">
                  Avvisi ({importResult.total_warnings})
                </div>
                <ul className="space-y-1 text-sm text-[#6E5113] bg-[#FFF7E4] border border-[#F3E1A8] rounded p-3">
                  {(importResult.warnings || []).map((w, i) => (
                    <li key={i} className="leading-snug">
                      {w}
                    </li>
                  ))}
                  {importResult.total_warnings > (importResult.warnings || []).length && (
                    <li className="opacity-70 pt-1 border-t border-[#F3E1A8]">
                      … e altri {importResult.total_warnings - (importResult.warnings || []).length} avvisi non mostrati.
                    </li>
                  )}
                </ul>
              </div>
            )}
            {(importResult?.total_errors ?? 0) > 0 && (
              <div data-testid="import-result-errors">
                <div className="text-xs font-semibold text-[#8A2E2E] uppercase tracking-wider mb-2">
                  Errori ({importResult.total_errors})
                </div>
                <ul className="space-y-1 text-sm text-[#8A2E2E] bg-[#FBEAEA] border border-[#EFC5C5] rounded p-3">
                  {(importResult.errors || []).map((e, i) => (
                    <li key={i} className="leading-snug">
                      {e}
                    </li>
                  ))}
                  {importResult.total_errors > (importResult.errors || []).length && (
                    <li className="opacity-70 pt-1 border-t border-[#EFC5C5]">
                      … e altri {importResult.total_errors - (importResult.errors || []).length} errori non mostrati.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setImportResult(null)}
              className="bg-[#2E4A3B] hover:bg-[#243c2f] text-white"
              data-testid="import-result-close-btn"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
